import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import { Chip, IconButton, Tooltip } from "@mui/material";
import { SimpleTreeView, TreeItem } from "@mui/x-tree-view";
import { useMemo } from "react";

import MDBox from "@/components/atoms/MDBox";
import MDTypography from "@/components/atoms/MDTypography";
import { MAX_CATEGORY_HIERARCHY_LEVEL } from "@/constants/products.constants";
import usePopOver from "@/hooks/use-popover";
import ICategory from "@/interfaces/product.category.interfaces";
import { ProductCategoriesMessages } from "@/messages/product-categories/product-categories.messages";

import { Popover } from "../CategoryPopover";

type Props = {
  categories: ICategory[];
  messages: ProductCategoriesMessages;
};

function CategoryTree({ categories, messages }: Props) {
  const { openPopover, anchorEl, closePopover, isOpen, selected } = usePopOver<ICategory>();

  const uniqueCategories = useMemo(() => {
    if (!categories || categories.length === 0) return [];

    const seen = new Set();
    const deduplicatedCategories = categories.filter((category) => {
      const duplicate = seen.has(category.id);
      seen.add(category.id);
      return !duplicate;
    });

    return deduplicatedCategories;
  }, [categories]);

  const RenderTree = (parentNode: ICategory | null, allCategories: ICategory[]) => {
    const childrenCategories = allCategories.filter((cat) => {
      if (!parentNode) {
        return cat.parentCode === null || cat.parentCode === "";
      }

      return cat.parentCode === parentNode.code;
    });

    return childrenCategories.map((category) => (
      <TreeItem
        itemId={category.id.toString()}
        label={
          <MDBox
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            pr={3}
            pl={3}
            sx={{ cursor: "pointer" }}
          >
            <MDBox display="flex" alignItems="center" justifyContent="space-between" gap={0.5}>
              {category.parentCode ? (
                <RadioButtonCheckedIcon color="primary" />
              ) : (
                <RadioButtonUncheckedIcon />
              )}
              <MDTypography
                variant="body2"
                fontWeight={"light"}
              >{`${category.description}`}</MDTypography>

              {category.level === MAX_CATEGORY_HIERARCHY_LEVEL && (
                <Tooltip title="Limite máximo de hierarquia atingido">
                  <Chip label="Máx" size="small" color="error" variant="outlined" sx={{ ml: 1 }} />
                </Tooltip>
              )}
            </MDBox>

            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                openPopover(e, category);
              }}
            >
              <MoreHorizIcon />
            </IconButton>
          </MDBox>
        }
        key={category.id}
        slots={{ expandIcon: ArrowRightIcon, collapseIcon: ArrowDropDownIcon }}
      >
        {RenderTree(category, allCategories)}
      </TreeItem>
    ));
  };

  const allNodeIds = uniqueCategories.map((category) => category.id.toString());

  return (
    <SimpleTreeView defaultExpandedItems={allNodeIds}>
      {RenderTree(null, uniqueCategories)}

      {selected && (
        <Popover
          isOpen={isOpen}
          anchorEl={anchorEl}
          onClose={closePopover}
          messages={messages}
          selected={selected}
          categories={uniqueCategories ?? []}
        />
      )}
    </SimpleTreeView>
  );
}

export default CategoryTree;
